import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Calculator, MapPin, FileText, Calendar, X, ChevronLeft, ChevronRight, DollarSign } from "lucide-react";
import { NeumorphicContainer } from "@/components/neumorphic";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ADUFeatureBox } from "@/components/ADUFeatureBox";
import { ModelBox } from "@/components/ModelBox";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

// Declare Calendly types for TypeScript
declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
    };
  }
}
interface CalculatorFormData {
  aduType: string;
  county: string;
  city: string;
  purposes: string[];
  otherPurpose: string;
  designChoice: string;
  squareFootage: string;
  finishLevel: string;
  firstName: string;
  lastName: string;
  propertyAddress: string;
  email: string;
  phone: string;
}
const LandingPage = () => {
  let theme = "dark";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not within NeumorphicThemeProvider, use default
  }

  const [activeFeature, setActiveFeature] = useState("overview");
  const [selectedCity, setSelectedCity] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [sentVerificationCode, setSentVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();

  // Theme-aware style helpers
  const headingClass = theme === "dark" ? "text-white" : "text-gray-900";
  const labelClass = theme === "dark" ? "text-white" : "text-gray-900";
  const textMutedClass = theme === "dark" ? "text-gray-300" : "text-gray-600";
  const getInputStyles = () => {
    return theme === "dark" 
      ? "bg-[#0a0a0a] border-0 text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] rounded-xl" 
      : "bg-white border-0 text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] rounded-xl";
  };
  const getOptionStyles = (isSelected: boolean) => {
    if (isSelected) {
      return 'bg-[#e4683b] text-white';
    }
    return theme === "dark" 
      ? 'bg-[#0a0a0a] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]'
      : 'bg-white text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#c0c0c0,-8px_-8px_16px_#ffffff]';
  };
  const getCardStyles = () => {
    return theme === "dark" 
      ? "bg-[#0a0a0a] border-0 shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] rounded-xl"
      : "bg-white border-0 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] rounded-xl";
  };
  const getSectionBg = () => {
    return theme === "dark" ? "bg-[#0a0a0a]" : "bg-[#f5f5f5]";
  };
  const [calculatorFormData, setCalculatorFormData] = useState<CalculatorFormData>({
    aduType: "",
    county: "",
    city: "",
    purposes: [],
    otherPurpose: "",
    designChoice: "",
    squareFootage: "",
    finishLevel: "",
    firstName: "",
    lastName: "",
    propertyAddress: "",
    email: "",
    phone: ""
  });
  const models = [{
    id: 1,
    name: "LA WEE",
    squareFootage: 525,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506005&miniplan=original",
    images: {
      "2D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
      "3D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
      exterior: ["/lovable-uploads/c129b40b-8432-491f-a1af-a47d817aeee4.png"],
      interior: []
    }
  }, {
    id: 2,
    name: "LA MEDIAN",
    squareFootage: 854,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506008&miniplan=original",
    images: {
      "2D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
      "3D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
      exterior: ["/lovable-uploads/b616a741-04ec-4dfd-82b8-6311f8a5d3b8.png"],
      interior: []
    }
  }, {
    id: 3,
    name: "LA GRAND",
    squareFootage: 1190,
    beds: 2,
    baths: 2,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
      "3D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
      exterior: ["/lovable-uploads/1125af17-6a9b-45a5-8c3a-f3a53e6daccd.png"],
      interior: []
    }
  }];

  // Exclusive models from UDA Models page
  const exclusiveModels = [{
    id: 4,
    name: "Gilmour",
    squareFootage: 378,
    beds: 0,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0910924-d4cb-4b53-8131-e7364b762518.png"],
      "3D": ["/lovable-uploads/0114c63b-5361-41d4-8633-feac775819c0.png"],
      exterior: ["/lovable-uploads/415954b9-1a79-470f-bf8b-3f5981dc17da.png"],
      interior: []
    }
  }, {
    id: 5,
    name: "Di Meola",
    squareFootage: 389,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0402f4be-93a9-4e6f-83c9-f390ea2aa5b6.png"],
      "3D": ["/lovable-uploads/045c9713-fc7d-47ab-8f98-5ee5bdd0aeb5.png"],
      exterior: ["/lovable-uploads/09797886-664e-4aa9-b648-d23241116ddd.png"],
      interior: []
    }
  }, {
    id: 6,
    name: "Satriani",
    squareFootage: 541,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/07742ba5-337c-4155-8432-cece1acdbe88.png"],
      "3D": ["/lovable-uploads/07dfc299-225b-4d3c-b4c3-7ae077c4b1ee.png"],
      exterior: ["/lovable-uploads/f55e0610-bfdd-4421-a78d-6077b3d8a540.png"],
      interior: []
    }
  }, {
    id: 7,
    name: "Knopfler",
    squareFootage: 797,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0a60f49b-a0b6-4dd4-9257-d06f9428f393.png"],
      "3D": ["/lovable-uploads/0b8f05f0-c562-4dc2-906f-07f942e98dc1.png"],
      exterior: ["/lovable-uploads/81450f32-921a-4836-9e5f-f88339547387.png"],
      interior: []
    }
  }, {
    id: 8,
    name: "Moore",
    squareFootage: 815,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0fb32522-99b0-4ad7-8f82-6664dad62226.png"],
      "3D": ["/lovable-uploads/104e2d5e-2f29-44da-859e-968bce9636e9.png"],
      exterior: ["/lovable-uploads/37873af0-0fc2-4ca9-8ea0-1049dd49a3de.png"],
      interior: []
    }
  }];
  useEffect(() => {
    // Load Calendly script
    const calendlyScript = document.createElement('script');
    calendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
    calendlyScript.async = true;
    document.head.appendChild(calendlyScript);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (document.head.contains(calendlyScript)) {
        document.head.removeChild(calendlyScript);
      }
    };
  }, []);

  // Fetch cities for regulations dropdown
  const {
    data: cities
  } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('cities').select('*').order('name');
      if (error) throw error;
      return data;
    }
  });

  // Fetch city regulations
  const {
    data: regulations,
    isLoading: regulationsLoading
  } = useQuery({
    queryKey: ['city_regulations', selectedCity],
    queryFn: async () => {
      if (!selectedCity) return [];
      const {
        data,
        error
      } = await supabase.from('city_regulations').select('*').eq('city_id', selectedCity);
      if (error) throw error;
      return data;
    },
    enabled: !!selectedCity
  });
  const features = [{
    id: "overview",
    title: "ADU Overview",
    icon: <Home />,
    description: "Learn the basics of Accessory Dwelling Units"
  }, {
    id: "calculator",
    title: "Cost Calculator",
    icon: <Calculator />,
    description: "Estimate your ADU project costs"
  }, {
    id: "regulations",
    title: "Local Regulations",
    icon: <MapPin />,
    description: "City-specific ADU requirements"
  }, {
    id: "financing",
    title: "Financing",
    icon: <DollarSign />,
    description: "Loan options and financial benefits"
  }, {
    id: "permits",
    title: "Our Process",
    icon: <FileText />,
    description: "Navigate our ADU process"
  }, {
    id: "consultation",
    title: "Free Consultation",
    icon: <Calendar />,
    description: "Book a call with our experts"
  }];
  const processSteps = ["Book a Free Consultation — Schedule right from our site", "Zoom Intro Call — Discuss budget, layout, and goals", "On-Site Meeting — Walk the site with our experts", "Custom ADU Proposal — Transparent, itemized quote", "Agreement Signed — Kickoff begins", "Design + Permits — We handle it all, A to Z", "Permit Approval — We manage all city/county submissions", "Construction Phase — From trench to trim", "Welcome Your ADU — A move-in-ready space, done right"];
  const counties = ["San Mateo County", "Santa Clara County"];
  const calculatorCities = {
    "San Mateo County": {
      tier1: ["Atherton", "Belmont", "Hillsborough", "Los Altos", "Los Altos Hills", "Menlo Park", "Palo Alto", "Portola Valley", "Woodside"],
      tier2: ["City of San Mateo", "County of San Mateo", "Redwood City", "San Carlos"]
    },
    "Santa Clara County": {
      tier1: ["Los Gatos", "Monte Sereno"],
      tier2: ["Campbell", "City of Santa Clara", "County of Santa Clara", "Cupertino", "San Jose", "Saratoga", "Sunnyvale", "Mountain View"]
    }
  };
  const aduTypes = ["Detached ADU", "Attached ADU", "Junior ADU (JADU)", "Garage Conversion", "Basement Conversion"];
  const purposeOptions = ["Additional income / rental property", "Housing for elderly parents", "Housing for young adults", "Guest house", "Home office / studio", "Future downsizing / personal use"];
  const designOptions = ["I already have approved plans/permits", "I'm working with an architect", "I'd like a fully custom ADU from UDA", "I'd prefer to choose from UDA's pre-designed plans", "Not sure yet"];
  const finishLevels = [{
    value: "standard",
    label: "Standard",
    cost: 99
  }, {
    value: "elite",
    label: "Elite",
    cost: 198
  }, {
    value: "deluxe",
    label: "Deluxe",
    cost: 297
  }];
  const openCalendlyModal = () => {
    console.log('Opening Calendly modal');
    setShowCalendly(true);
  };
  const closeCalendlyModal = () => {
    console.log('Closing Calendly modal');
    setShowCalendly(false);
  };
  const handleBookConsultation = () => {
    openCalendlyModal();
  };

  // Calculator functions
  const calculateCost = () => {
    const sf = parseInt(calculatorFormData.squareFootage);
    let designBuildRate = 0;
    const isTier1 = calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier1.includes(calculatorFormData.city);
    if (sf < 400) {
      designBuildRate = isTier1 ? 498 : 448;
    } else if (sf <= 800) {
      designBuildRate = isTier1 ? 488 : 438;
    } else {
      designBuildRate = isTier1 ? 478 : 428;
    }
    const finishLevel = finishLevels.find(level => level.value === calculatorFormData.finishLevel);
    const materialRate = finishLevel?.cost || 99;
    const totalCost = (designBuildRate + materialRate) * sf;
    setCalculatedCost(totalCost);
  };
  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('send-verification-sms', {
        body: {
          phoneNumber: calculatorFormData.phone
        }
      });
      if (error) throw error;
      if (data.success) {
        setSentVerificationCode(data.verificationCode);
        setIsCodeSent(true);
        toast({
          title: "Verification code sent",
          description: `A 6-digit code has been sent to ${calculatorFormData.phone}`
        });
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Error",
        description: "Failed to send verification code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSendingCode(false);
    }
  };
  const verifyCode = () => {
    if (verificationCode === sentVerificationCode) {
      setIsVerified(true);
      calculateCost();
      setShowResult(true);
      toast({
        title: "Phone verified successfully",
        description: "Your phone number has been verified!"
      });
    } else {
      toast({
        title: "Invalid code",
        description: "The verification code you entered is incorrect. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleNext = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    } else {
      if (!isCodeSent) {
        sendVerificationCode();
      } else {
        verifyCode();
      }
    }
  };
  const handleBack = () => {
    if (currentStep === 7 && currentStep > 6) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handlePurposeChange = (purpose: string, checked: boolean) => {
    if (checked) {
      setCalculatorFormData(prev => ({
        ...prev,
        purposes: [...prev.purposes, purpose]
      }));
    } else {
      setCalculatorFormData(prev => ({
        ...prev,
        purposes: prev.purposes.filter(p => p !== purpose)
      }));
    }
  };
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return calculatorFormData.aduType !== "";
      case 2:
        return calculatorFormData.county !== "" && calculatorFormData.city !== "";
      case 3:
        return calculatorFormData.purposes.length > 0;
      case 4:
        return calculatorFormData.designChoice !== "";
      case 5:
        return calculatorFormData.squareFootage !== "" && calculatorFormData.finishLevel !== "";
      case 6:
        return calculatorFormData.firstName !== "" && calculatorFormData.lastName !== "" && calculatorFormData.propertyAddress !== "" && calculatorFormData.email !== "" && calculatorFormData.phone !== "";
      case 7:
        return !isCodeSent || isCodeSent && verificationCode.length === 6;
      default:
        return false;
    }
  };
  const renderCalculatorStep = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Type of ADU</h2>
            <div className="grid gap-3">
              {aduTypes.map(type => <label key={type} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${getOptionStyles(calculatorFormData.aduType === type)}`}>
                  <input type="radio" name="aduType" value={type} checked={calculatorFormData.aduType === type} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                aduType: e.target.value
              }))} className="w-4 h-4 text-[#e4683b] accent-[#e4683b]" />
                  <span className="text-sm md:text-base">{type}</span>
                </label>)}
            </div>
          </div>;
      case 2:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Select Your County and City</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="county" className={labelClass}>County</Label>
                <Select value={calculatorFormData.county} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                county: value,
                city: ""
              }))}>
                  <SelectTrigger className={getInputStyles()}>
                    <SelectValue placeholder="Select County" />
                  </SelectTrigger>
                  <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                    {counties.map(county => <SelectItem key={county} value={county}>{county}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {calculatorFormData.county && <div>
                  <Label htmlFor="city" className={labelClass}>City</Label>
                  <Select value={calculatorFormData.city} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                city: value
              }))}>
                    <SelectTrigger className={getInputStyles()}>
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                      {calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier1.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                      {calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier2.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>}
            </div>
          </div>;
      case 3:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Purpose of ADU</h2>
            <div className="space-y-3">
              {purposeOptions.map(purpose => <div key={purpose} className={`flex items-center space-x-2 p-3 rounded-xl ${getCardStyles()}`}>
                  <Checkbox id={purpose} checked={calculatorFormData.purposes.includes(purpose)} onCheckedChange={checked => handlePurposeChange(purpose, checked as boolean)} className="border-gray-500" />
                  <Label htmlFor={purpose} className={`text-sm md:text-base cursor-pointer ${labelClass}`}>{purpose}</Label>
                </div>)}
              <div className={`flex items-center space-x-2 p-3 rounded-xl ${getCardStyles()}`}>
                <Checkbox id="other" checked={calculatorFormData.purposes.includes("Other")} onCheckedChange={checked => {
                if (checked) {
                  setCalculatorFormData(prev => ({
                    ...prev,
                    purposes: [...prev.purposes, "Other"]
                  }));
                } else {
                  setCalculatorFormData(prev => ({
                    ...prev,
                    purposes: prev.purposes.filter(p => p !== "Other"),
                    otherPurpose: ""
                  }));
                }
              }} className="border-gray-500" />
                <Label htmlFor="other" className={`text-sm md:text-base cursor-pointer ${labelClass}`}>Other</Label>
              </div>
              {calculatorFormData.purposes.includes("Other") && <Input placeholder="Please specify..." value={calculatorFormData.otherPurpose} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              otherPurpose: e.target.value
            }))} className={`mt-2 ${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />}
            </div>
          </div>;
      case 4:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Design & Permitting</h2>
            <div className="grid gap-3">
              {designOptions.map(option => <label key={option} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${getOptionStyles(calculatorFormData.designChoice === option)}`}>
                  <input type="radio" name="designChoice" value={option} checked={calculatorFormData.designChoice === option} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                designChoice: e.target.value
              }))} className="w-4 h-4 text-[#e4683b] accent-[#e4683b]" />
                  <span className="text-sm md:text-base">{option}</span>
                </label>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Square Footage and Finish Level</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="squareFootage" className={labelClass}>Square Footage</Label>
                <Input id="squareFootage" type="number" placeholder="Enter square footage" value={calculatorFormData.squareFootage} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                squareFootage: e.target.value
              }))} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl placeholder:text-gray-500`} />
              </div>
              
              <div>
                <Label htmlFor="finishLevel" className={labelClass}>Finish Material Category</Label>
                <Select value={calculatorFormData.finishLevel} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                finishLevel: value
              }))}>
                  <SelectTrigger className={getInputStyles()}>
                    <SelectValue placeholder="Select finish level" />
                  </SelectTrigger>
                  <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                    {finishLevels.map(level => <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className={labelClass}>First Name *</Label>
                <Input id="firstName" value={calculatorFormData.firstName} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                firstName: e.target.value
              }))} required className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
              </div>
              <div>
                <Label htmlFor="lastName" className={labelClass}>Last Name *</Label>
                <Input id="lastName" value={calculatorFormData.lastName} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                lastName: e.target.value
              }))} required className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
              </div>
            </div>
            <div>
              <Label htmlFor="propertyAddress" className={labelClass}>Property Address *</Label>
              <Input id="propertyAddress" value={calculatorFormData.propertyAddress} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              propertyAddress: e.target.value
            }))} required className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
            </div>
            <div>
              <Label htmlFor="email" className={labelClass}>Email Address *</Label>
              <Input id="email" type="email" value={calculatorFormData.email} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              email: e.target.value
            }))} required className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
            </div>
            <div>
              <Label htmlFor="phone" className={labelClass}>Phone Number *</Label>
              <Input id="phone" type="tel" value={calculatorFormData.phone} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              phone: e.target.value
            }))} required className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
            </div>
          </div>;
      case 7:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Phone Verification</h2>
            {!isCodeSent ? <div className="text-center space-y-4">
                <p className={textMutedClass}>
                  We'll send a verification code to {calculatorFormData.phone} to verify your phone number.
                </p>
              </div> : <div className="space-y-4">
                <p className={`text-center ${textMutedClass}`}>
                  Enter the 6-digit verification code sent to {calculatorFormData.phone}
                </p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode}>
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot index={0} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                      <InputOTPSlot index={1} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                      <InputOTPSlot index={2} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                      <InputOTPSlot index={3} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                      <InputOTPSlot index={4} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                      <InputOTPSlot index={5} className={`${theme === "dark" ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]" : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#c0c0c0,inset_-4px_-4px_8px_#ffffff]"} rounded-xl`} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="text-center">
                  <Button variant="link" onClick={() => {
                setIsCodeSent(false);
                setVerificationCode("");
                setSentVerificationCode("");
              }} disabled={isSendingCode} className="text-[#e4683b] hover:text-[#e4683b]/80">
                    Didn't receive the code? Send again
                  </Button>
                </div>
              </div>}
          </div>;
      default:
        return null;
    }
  };
  const renderFeatureContent = () => {
    switch (activeFeature) {
      case "overview":
        return <div className="space-y-6">
            <h2 className={`text-4xl font-normal ${headingClass}`}>What is an ADU?</h2>
            <p className={`text-lg ${textMutedClass}`}>
              An Accessory Dwelling Unit (ADU) is a secondary housing unit on a single-family 
              residential lot. ADUs can be attached to or detached from the main house.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className={getCardStyles()}>
                <CardHeader>
                  <CardTitle className={headingClass}>Types of ADUs</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className={`space-y-2 ${textMutedClass}`}>
                    <li>• Detached ADUs</li>
                    <li>• Attached ADUs</li>
                    <li>• Garage Conversions</li>
                    <li>• Basement Conversions</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className={getCardStyles()}>
                <CardHeader>
                  <CardTitle className={headingClass}>Benefits</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className={`space-y-2 ${textMutedClass}`}>
                    <li>• Additional rental income</li>
                    <li>• Increased property value</li>
                    <li>• Housing for family</li>
                    <li>• Flexible living space</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>;
      case "calculator":
        return <div className="space-y-6">
            <h2 className={`font-bold text-2xl mx-[22px] ${headingClass}`}>ADU Cost Calculator</h2>
            <Card className={getCardStyles()}>
              <CardHeader className="text-center">
                <CardTitle className={`flex items-center justify-center gap-2 md:text-3xl text-xl ${headingClass}`}>
                  <Calculator className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  UDA ADU Calculator
                </CardTitle>
                <div className="flex justify-center mt-4">
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-3 h-3 rounded-full ${step === currentStep ? "bg-[#e4683b]" : step < currentStep ? "bg-[#e4683b]/60" : theme === "dark" ? "bg-gray-600" : "bg-gray-300"}`} />)}
                  </div>
                </div>
                <p className={`text-sm mt-2 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Step {currentStep} of 7</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {renderCalculatorStep()}
                
                <div className="flex justify-between pt-6">
                  <Button variant="secondary" onClick={handleBack} disabled={currentStep <= 6} className={`flex items-center gap-2 border-0 disabled:opacity-50 disabled:shadow-none rounded-xl ${theme === "dark" ? "bg-[#0a0a0a] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]" : "bg-white text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#c0c0c0,-8px_-8px_16px_#ffffff]"}`}>
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button onClick={handleNext} disabled={!isStepValid() || isSendingCode} className={`flex items-center gap-2 bg-[#e4683b] hover:bg-[#e4683b]/80 text-white rounded-xl ${theme === "dark" ? "shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}>
                    {currentStep === 7 ? !isCodeSent ? isSendingCode ? "Sending..." : "Send Verification Code" : "Verify & View Price" : "Next"}
                    {currentStep < 7 && <ChevronRight className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      case "regulations":
        return <div className="space-y-6">
            <h2 className={`text-3xl font-bold ${headingClass}`}>Local ADU Regulations</h2>
            <div className="space-y-4">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className={getInputStyles()}>
                  <SelectValue placeholder="Select your city" />
                </SelectTrigger>
                <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                  {cities?.map(city => <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              
              {regulationsLoading && <p className={textMutedClass}>Loading regulations...</p>}
              
              {regulations && regulations.length > 0 && <div className="space-y-4">
                  {regulations.map(regulation => <Card key={regulation.id} className={getCardStyles()}>
                      <CardHeader>
                        <CardTitle className={`text-lg ${headingClass}`}>{regulation.category}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className={`mb-2 ${textMutedClass}`}>{regulation.description}</p>
                        <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{regulation.details}</p>
                      </CardContent>
                    </Card>)}
                </div>}
            </div>
          </div>;
      case "financing":
        return <div className="space-y-6">
            <h2 className={`text-3xl font-bold ${headingClass}`}>ADU Financing Options</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className={getCardStyles()}>
                <CardHeader>
                  <CardTitle className="text-xl text-[#e4683b]">Loan Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Home Equity Loans (HELOCs)</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Borrow against home equity with flexible repayment.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Cash-Out Refinancing</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Refinance your mortgage to fund construction.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Construction Loans</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Short-term loans for ADU projects.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>ADU Grant Programs</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Government funding and incentives.</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={getCardStyles()}>
                <CardHeader>
                  <CardTitle className="text-xl text-[#e4683b]">Financial Benefits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>ROI Analysis</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Understand potential returns before building.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Monthly Payment Estimates</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Breakdown of financing costs.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Rental Income Potential</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>Estimated earnings from rental opportunities.</p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${headingClass}`}>Property Value Increase</h4>
                    <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>ADUs can add long-term value to your home.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className={`mt-6 ${getCardStyles()}`}>
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <h3 className={`text-lg font-semibold ${headingClass}`}>Ready to Explore Your Financing Options?</h3>
                  <p className={theme === "dark" ? "text-gray-400" : "text-gray-500"}>Schedule a consultation to discuss the best financing strategy for your ADU project.</p>
                  <Button className={`bg-[#e4683b] hover:bg-[#e4683b]/80 text-white rounded-xl ${theme === "dark" ? "shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`} onClick={handleBookConsultation}>
                    Discuss Financing Options
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      case "permits":
        return <div className="space-y-6">
            <h2 className={`text-3xl font-bold ${headingClass}`}>Our Process</h2>
            <Card className={getCardStyles()}>
              <CardHeader className="md:p-6 p-4">
                <CardTitle className={`flex items-center gap-2 text-2xl md:text-3xl text-center ${headingClass}`}>
                  <Calendar className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  Our Process
                </CardTitle>
              </CardHeader>
              <CardContent className="md:p-6 p-4 pt-0">
                <div className="space-y-3 md:space-y-4">
                  {processSteps.map((step, index) => <div key={index} className="flex items-start gap-3 md:gap-4">
                      <div className={`rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-xs md:text-sm font-bold flex-shrink-0 ${theme === "dark" ? "bg-[#0a0a0a] text-white shadow-[4px_4px_8px_#000000,-4px_-4px_8px_#141414]" : "bg-white text-gray-900 shadow-[4px_4px_8px_#c0c0c0,-4px_-4px_8px_#ffffff]"}`}>
                        {index + 1}
                      </div>
                      <span className={`leading-relaxed text-sm md:text-base ${textMutedClass}`}>{step}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>;
      case "consultation":
        return <div className="space-y-6">
            <h2 className={`text-3xl font-bold ${headingClass}`}>Book Free Consultation</h2>
            <Card className={getCardStyles()}>
              <CardHeader>
                <CardTitle className={headingClass}>Expert ADU Guidance</CardTitle>
                <CardDescription className={theme === "dark" ? "text-gray-400" : "text-gray-500"}>Schedule a free 30-minute consultation </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className={textMutedClass}>Our experts can help you with:</p>
                  <ul className={`space-y-2 ml-4 ${textMutedClass}`}>
                    <li>• Feasibility assessment</li>
                    <li>• Design recommendations</li>
                    <li>• Permit guidance</li>
                    <li>• Cost estimation</li>
                    <li>• Contractor connections</li>
                  </ul>
                  <Button onClick={handleBookConsultation} className={`w-full text-3xl font-light rounded-xl ${theme === "dark" ? "bg-[#0a0a0a] hover:bg-[#141414] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "bg-white hover:bg-gray-50 text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}>
                    Book Your Free Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      default:
        return <div className="space-y-6">
            <h2 className={`text-3xl font-bold ${headingClass}`}>Feature Coming Soon</h2>
            <p className={`text-lg ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              This feature is currently under development. Check back soon!
            </p>
          </div>;
    }
  };
  return (
    <NeumorphicContainer>
      <div className={`min-h-screen transition-colors duration-500 ${getSectionBg()}`}>
      <CanonicalTag url="https://www.udaadu.com/landing" />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      
      {/* Large Recessed Neumorphic Container */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className={`rounded-3xl p-8 md:p-12 transition-all duration-500 ${theme === "dark" ? "shadow-[inset_12px_12px_24px_#000000,inset_-12px_-12px_24px_#141414] bg-[#0a0a0a]" : "shadow-[inset_12px_12px_24px_#c0c0c0,inset_-12px_-12px_24px_#ffffff] bg-[#f5f5f5]"}`}>
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl mb-8 text-[#e4683b] italic lg:text-4xl font-normal"> UDA Wizard, anything and everything ADU.</h1>
            
            <p className={`text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12 ${headingClass}`}>
              Discover your property's potential with our advanced ADU analysis tool
            </p>

            {/* Hero Video Section */}
            <div className="mb-12 max-w-4xl md:max-w-3xl mx-auto">
              <div className={`aspect-video overflow-hidden rounded-xl ${theme === "dark" ? "shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414] bg-[#0a0a0a]" : "shadow-[inset_8px_8px_16px_#c0c0c0,inset_-8px_-8px_16px_#ffffff] bg-[#f5f5f5]"}`}>
                <video
                  key="hero-video"
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/It's%20all%20starting%20from%20here.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvSXQncyBhbGwgc3RhcnRpbmcgZnJvbSBoZXJlLm1wNCIsImlhdCI6MTc1MzMzNjY1OCwiZXhwIjoyMDY4Njk2NjU4fQ.zwIVuHxRec2UKw3tzefYEsgHH2zjdlzydnUU9r0lbpY"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Add large "What fits my lot?" title */}
            <h2 className="text-3xl md:text-4xl mb-8 text-[#e4683b] lg:text-4xl font-normal">
              What fits my lot?
            </h2>
            
            <div className="w-full max-w-6xl mx-auto">
              <a 
                href="/what-fits-my-lot"
                className="inline-block px-8 py-4 bg-[#E4683B] text-white rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Explore Your Property's ADU Potential
              </a>
            </div>
          </div>

          {/* Pre-Approved Models Section */}
          <div className="py-12">
            <h2 className="text-3xl md:text-4xl text-center mb-12 text-[#e4683b] lg:text-4xl font-normal">Pre-Approved &amp; Permit Ready Plans</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
              {models.map((model) => (
                <ModelBox 
                  key={model.id} 
                  model={model}
                />
              ))}
            </div>
          </div>

          {/* Our Exclusive Pre-designed Models Section */}
          <div className="py-12">
            <h2 className="text-3xl md:text-4xl text-center mb-12 text-[#e4683b] lg:text-4xl font-normal">Our Exclusive Pre-designed Models</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 max-w-6xl mx-auto">
              {exclusiveModels.map((model) => (
                <ModelBox 
                  key={model.id} 
                  model={model}
                />
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Feature Dashboard Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {features.map((feature) => (
              <ADUFeatureBox 
                key={feature.id} 
                title={feature.title} 
                icon={feature.icon} 
                description={isMobile ? undefined : feature.description} 
                isActive={activeFeature === feature.id} 
                onClick={() => setActiveFeature(feature.id)} 
              />
            ))}
          </div>

          {/* Feature Content */}
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeFeature} 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              exit={{ opacity: 0, x: -20 }} 
              transition={{ duration: 0.3 }} 
              className={`rounded-2xl p-8 ${theme === "dark" ? "bg-[#0a0a0a] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "bg-white shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}
            >
              {renderFeatureContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* FAQ Button */}
      <section className="py-4 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Button 
            onClick={() => navigate('/faq')} 
            className={`text-3xl font-light mx-0 px-[79px] py-[30px] rounded-xl ${theme === "dark" ? "bg-[#0a0a0a] hover:bg-[#141414] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "bg-white hover:bg-gray-50 text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}
          >
            FAQ
          </Button>
        </div>
      </section>

      {/* Main Video Section */}
      <section className={`py-12 px-4 ${getSectionBg()}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Original Video */}
            <div className={`relative rounded-xl overflow-hidden ${theme === "dark" ? "shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]" : "shadow-[inset_8px_8px_16px_#c0c0c0,inset_-8px_-8px_16px_#ffffff]"}`}>
              <video 
                className="w-full rounded-lg" 
                autoPlay 
                loop 
                muted 
                playsInline 
                onContextMenu={(e) => e.preventDefault()} 
                style={{ pointerEvents: 'none' }}
              >
                <source 
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/UDA%20ADU%20MAIN%20PAGE.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvVURBIEFEVSBNQUlOIFBBR0UubXA0IiwiaWF0IjoxNzQ5Njc1OTkzLCJleHAiOjIwNjUwMzU5OTN9.ZqXTGIc_jiNHFo-rqapg8iYsXVKNeOkJPj9lfkW6sjw" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* New Video */}
            <div className={`relative rounded-xl overflow-hidden ${theme === "dark" ? "shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]" : "shadow-[inset_8px_8px_16px_#c0c0c0,inset_-8px_-8px_16px_#ffffff]"}`}>
              <video 
                className="w-full rounded-lg" 
                autoPlay 
                loop 
                muted 
                playsInline 
                onContextMenu={(e) => e.preventDefault()} 
                style={{ pointerEvents: 'none' }}
              >
                <source 
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/LA%20MEDIAN%20SHORT%20CLIP%20%201%202.mov?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvTEEgTUVESUFOIFNIT1JUIENMSVAgIDEgMi5tb3YiLCJpYXQiOjE3NTMxNjUxNzcsImV4cCI6MjA2ODUyNTE3N30.0myJ1OSGYl_GNuDayMKyC3ILlMpdLF0bOyNzfONQQJQ" 
                  type="video/quicktime" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Result Dialog */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Your ADU Cost Estimate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Calendly Widget */}
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-4 text-center">Schedule Your Free Consultation</h3>
              <div className="w-full h-[500px] border rounded-lg">
                <iframe 
                  src="https://calendly.com/udaadu" 
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  title="Schedule Consultation" 
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
            
            {/* Cost Estimate */}
            <div className="text-center space-y-4">
              <div className="bg-[#e4683b]/10 p-6 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Your complete design, build, and material cost for the entire project starts at:</p>
                <p className="text-3xl font-bold text-[#e4683b]">
                  ${calculatedCost.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground mt-3 italic">
                  Your actual cost may vary slightly depending on site conditions and scope — every detail matters. We'll provide an exact, itemized quote during your complimentary ADU meeting.
                </p>
              </div>
              
              <div className="text-sm text-muted-foreground p-4 bg-accent rounded-lg">
                <p>We offer special discounts, grant options, and in-house financing to support our local community. By scheduling and showing up to your appointment with our CEO, you'll automatically receive a 5% discount, with additional benefits discussed based on your situation.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
      <FloatingADUWizard />
      <NeumorphicThemeToggle />
    </div>
    </NeumorphicContainer>
  );
};

export default LandingPage;
